import { Models } from "@/api/types";
import { formatCurrency } from "toolbox/Money";
import pluralize from "utils/pluralize";

export enum BillingPeriod {
  Daily = 0,
  Weekly = 1,
  Monthly = 2,
  Yearly = 3,
}

export function parseBillingPeriodName(
  value: number
): keyof typeof BillingPeriod {
  if (value in BillingPeriod) {
    return BillingPeriod[value as BillingPeriod] as keyof typeof BillingPeriod;
  }
  return "Monthly";
}

export const getBillingIntervalBase = (
  billingPeriodInterval: string | null
): string => {
  if (!billingPeriodInterval) return "month";

  const base = billingPeriodInterval.replace(/ly$/, "").toLowerCase();
  return base === "dai" ? "day" : base;
};

export const formatBillingInterval = (
  base: string,
  billingPeriodIntervalCount: number | null
): string => {
  if (!billingPeriodIntervalCount || billingPeriodIntervalCount === 1) {
    return base;
  }

  return `${billingPeriodIntervalCount} ${base}s`;
};

export const formatBillingPeriodInterval = (
  price: number,
  billingPeriodInterval: string | null,
  billingPeriodIntervalCount: number | null = 1
) => {
  const base = getBillingIntervalBase(billingPeriodInterval);
  const formattedInterval = formatBillingInterval(
    base,
    billingPeriodIntervalCount
  );

  return `${formatCurrency(price)}/${formattedInterval}`;
};

export const getBillingPeriodIntervalTreatmentSelection = (
  membershipBillingPeriodIntervalCount: number,
  membershipBillingPeriodInterval: string,
  isFlexibleMembershipsEnabled: boolean,
  periodCount: number,
  billingPeriodInterval: number | null = null
): string => {
  if (
    !isFlexibleMembershipsEnabled &&
    (!billingPeriodInterval || billingPeriodInterval <= 0)
  ) {
    return "";
  }

  if (isFlexibleMembershipsEnabled) {
    const base = getBillingIntervalBase(membershipBillingPeriodInterval);
    const singular = base.charAt(0).toUpperCase() + base.slice(1);
    const plural = `${singular}s`;

    if (periodCount === 1) {
      return singular;
    }

    return `${periodCount} ${plural}`;
  }

  switch (billingPeriodInterval) {
    case 12:
      return " YEAR!";
    case 1:
      return " MONTH!";
    default:
      return ` ${billingPeriodInterval} MONTHS!`;
  }
};

export const beautyBankText = (
  isFlexibleMembershipsEnabled: boolean,
  beautyBankCreditAmount: string,
  baseBillingPeriodInterval: string,
  membershipListing: { billingPeriodIntervalCount?: number }
) => {
  if (isFlexibleMembershipsEnabled) {
    return `Bank ${beautyBankCreditAmount} every 
      ${formatBillingInterval(
        baseBillingPeriodInterval,
        membershipListing.billingPeriodIntervalCount ?? 1
      )} for use on future services - spend anytime!`;
  }
  return `Bank ${beautyBankCreditAmount} monthly for use on future services - use anytime!`;
};

export const repeatCashEntryText = (
  relatedMembership: Models<"MembershipModel"> | null | undefined
) => {
  const parsedBillingPeriodInterval = parseBillingPeriodName(
    relatedMembership?.billingPeriodInterval ?? 1
  );
  const visibleMembershipBillingPeriodInterval = getBillingIntervalBase(
    parsedBillingPeriodInterval
  );
  const formatBillingPeriodInterval = formatBillingInterval(
    visibleMembershipBillingPeriodInterval,
    relatedMembership?.billingPeriodIntervalCount ?? 1
  );

  if ((relatedMembership?.billingPeriodIntervalCount ?? 1) > 1) {
    return `Every ${formatBillingPeriodInterval}`;
  }
  return `${parsedBillingPeriodInterval}`;
};
